import { captureDesktopObservationOwner } from '@/api/desktopSyncOwnership';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { useEffect, useSyncExternalStore } from 'react';

import {
  buildDesktopSyncContext,
  reportOrderDataStatus,
  runDesktopSyncBatch,
} from '@/api/desktopSyncCoordinator';
import { useAuth } from '@/context/AuthContext';
import { subscribeToReviewQueueChanges } from '@/utils/reviewQueueEvents';

export const SYNC_INTERVAL_MS = 60_000;

// Desktop-only: keeps the local order cache (recent past + future) and shop
// identity fresh while online so the app has data (and a faithful order sheet)
// to show during an outage. Renders nothing; no-op on the web.
export default function DesktopOrderSync() {
  // App mounts this beneath AuthProvider. The fallback keeps a future mounting
  // mistake from blanking the entire renderer before Login can appear.
  const { user, sessionBinding } = useAuth() || {};
  const userId = user?.id;
  const tenant = user?.tenant_id;
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const desktop = typeof window === 'undefined' ? null : window.lvbDesktop;
  const accepted = sessionBinding?.user === user && sessionBinding?.revision === revision && sessionBinding?.source === 'cloud';
  const backend = desktop?.backendUrl;
  const local = desktop?.localBackendUrl;
  const roleKey = Array.from(new Set(
    (Array.isArray(user?.roles) ? user.roles : [])
      .filter((role) => typeof role === 'string')
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean),
  )).sort().join('|');

  useEffect(() => {
    if (!desktop || !accepted) return undefined;
    const context = buildDesktopSyncContext({ id: userId, tenant_id: tenant,
      roles: roleKey ? roleKey.split('|') : [] });
    let active = true;
    const sync = () => { if (active) Promise.resolve(runDesktopSyncBatch(context)).catch(() => {}); };
    sync();
    const unsubscribe = subscribeToReviewQueueChanges(event => {
      if (!active || !context.owner?.current()) return;
      if (['offline_conflict_resolved', 'desktop_replay_completed'].includes(event.detail?.reason)) {
        Promise.resolve(desktop.synchronizeOrderData?.()).catch(() => {});
      }
    });
    const id = setInterval(sync, SYNC_INTERVAL_MS);
    return () => { active = false; clearInterval(id); unsubscribe(); context.owner?.retire(); };
  }, [userId, tenant, roleKey, revision, desktop, backend, local, accepted]);

  // Accepted order receipts are native installation facts, including before login/offline.
  useEffect(() => {
    if (!desktop) return undefined;
    const context = { owner: captureDesktopObservationOwner() };
    let active = true;
    const observe = status => {
      if (active) Promise.resolve(reportOrderDataStatus(status, context)).catch(() => {});
    };
    const unsubscribe = typeof desktop.onOrderDataStatusChanged === 'function'
      ? desktop.onOrderDataStatusChanged(observe) : null;
    if (typeof desktop.getOrderDataStatus === 'function') observe(null);
    return () => { active = false; unsubscribe?.(); context.owner.retire(); };
  }, [desktop, backend, local]);
  return null;
}
